import { useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { getMetricDimensionConfig } from "@main/pages/Release/helpers/metricDimensionConfig";

export default function useTestingCoverageScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.testing_coverage,
  );

  const [rows, setRows] = useState<DimensionRow[]>([]);
  const [weightA, setWeightA] = useState(0);
  const [weightB, setWeightB] = useState(0);
  const [savedWeightA, setSavedWeightA] = useState(0);
  const [savedWeightB, setSavedWeightB] = useState(0);

  useEffect(() => {
    const config = scoringConfig?.config;
    if (!config) return;

    const enabledDimensions: Record<string, boolean> = config.enabled_dimensions ?? {};
    const dimensionTargets = config.dimension_targets ?? {};
    const enabledMetadata = config.tracked_metadata_keys ?? [];
    const metadataTargets = config.metadata_targets ?? {};

    const dimensionRows: DimensionRow[] = Object.keys(enabledDimensions).map((key) => ({
      key,
      kind: "dimension",
      enabled: enabledDimensions[key],
      searchKey: getMetricDimensionConfig(key).searchKey,
      targets: toTargets(dimensionTargets[key]),
    }));

    const metadataRows: DimensionRow[] = enabledMetadata.map((key) => ({
      key,
      kind: "metadata",
      enabled: true,
      searchKey: getMetricDimensionConfig(key).searchKey,
      targets: toTargets(metadataTargets[key]),
    }));

    setRows([...dimensionRows, ...metadataRows]);

    if (config.min_weight !== undefined) {
      const a = Math.round(config.min_weight * 100);
      setWeightA(a);
      setSavedWeightA(a);
    }
    if (config.mean_weight !== undefined) {
      const b = Math.round(config.mean_weight * 100);
      setWeightB(b);
      setSavedWeightB(b);
    }
  }, [scoringConfig]);

  const weightSum = weightA + weightB;
  const weightError = weightSum !== 100 ? `a + b must equal 100%. It is currently ${weightSum}%.` : undefined;

  const buildPayload = (rowsSource: DimensionRow[], wA: number, wB: number) => {
    const dimensions = rowsSource.filter((row) => row.kind === "dimension");
    const metadata = rowsSource.filter((row) => row.kind === "metadata");

    return {
      enabled_dimensions: Object.fromEntries(dimensions.map((row) => [row.key, row.enabled])),
      tracked_metadata_keys: metadata.filter((row) => row.enabled).map((row) => row.key),
      dimension_targets: toTargetMap(dimensions),
      metadata_targets: toTargetMap(metadata),
      min_weight: wA / 100,
      mean_weight: wB / 100,
    };
  };

  const persist = (rowsSource: DimensionRow[]) => {
    saveScoringConfig(buildPayload(rowsSource, savedWeightA, savedWeightB), {
      onSuccess: () => {
        refetchReleaseStatus();
        displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
      },
      onError: () => {
        displayToast({ title: "Something went wrong", content: "Please try again." });
      },
    });
  };

  const toggleDimension = (key: string) => {
    const next = rows.map((row) => (row.key === key ? { ...row, enabled: !row.enabled } : row));
    setRows(next);
    persist(next);
  };

  const addCategory = (key: string, value: string) => {
    const next = rows.map((row) => {
      if (row.key !== key) return row;
      if (row.targets.some((t) => t.value === value)) return row;
      return { ...row, targets: [...row.targets, { value, minimum: 10 }] };
    });
    setRows(next);
    persist(next);
  };

  const removeCategory = (key: string, value: string) => {
    const next = rows.map((row) =>
      row.key === key ? { ...row, targets: row.targets.filter((t) => t.value !== value) } : row,
    );
    setRows(next);
    persist(next);
  };

  const setMinimum = (key: string, value: string, minimum: number) => {
    setRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? { ...row, targets: row.targets.map((t) => (t.value === value ? { ...t, minimum } : t)) }
          : row,
      ),
    );
  };

  const commitMinimum = () => persist(rows);

  const handleSave = () => {
    saveScoringConfig(buildPayload(rows, weightA, weightB), {
      onSuccess: () => {
        setSavedWeightA(weightA);
        setSavedWeightB(weightB);
        refetchReleaseStatus();
        displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
      },
      onError: () => {
        displayToast({ title: "Something went wrong", content: "Please try again." });
      },
    });
  };

  return {
    rows,
    toggleDimension,
    addCategory,
    removeCategory,
    setMinimum,
    commitMinimum,
    weightA,
    setWeightA,
    weightB,
    setWeightB,
    weightError,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

export interface DimensionTarget {
  value: string;
  minimum: number;
}

export interface DimensionRow {
  key: string;
  kind: "dimension" | "metadata";
  enabled: boolean;
  searchKey: string;
  targets: DimensionTarget[];
}

interface TargetPayload {
  value: string;
  minimum_sessions: number;
}

function toTargets(source?: TargetPayload[]): DimensionTarget[] {
  return (source ?? []).map((t) => ({ value: t.value, minimum: t.minimum_sessions ?? 1 }));
}

function toTargetMap(rows: DimensionRow[]): Record<string, TargetPayload[]> {
  const result: Record<string, TargetPayload[]> = {};
  for (const row of rows) {
    if (!row.targets.length) continue;
    result[row.key] = row.targets.map((t) => ({ value: t.value, minimum_sessions: t.minimum }));
  }
  return result;
}
